import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { UserProvider } from './UserContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navbar from './Navbar';
import LandingPage from './LandingPage';
import TablePredictions from './TablePredictions';
import MatchPredictions from './MatchPredictions';
import SignUp from './SignUp';
import Login from './Login';
import Performance from './Performance';
import NotFoundPage from './NotFoundPage';
import Leagues from './Leagues';
import MatchPredictorLeaderboard from './MatchPredictorLeaderboard';
import TablePredictorLeaderboard from './TablePredictorLeaderboard';
import ScoringExplained from './ScoringExplained';
import AccountManagement from './AccountManagement';

const ProtectedRoute = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-premier-purple"></div>
            </div>
        );
    }

    if (!user || !user.emailVerified) {
        return <Navigate to="/login" replace />;
    }

    return children;
};

const PublicOnlyRoute = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-premier-purple"></div>
            </div>
        );
    }

    if (user && user.emailVerified) {
        return <Navigate to="/match-predictor" replace />;
    }

    return children;
};

function App() {
    return (
        <AuthProvider>
            <UserProvider>
                <Router>
                    <div className="flex flex-col min-h-screen"> {/* Wrapper div for sticky footer */}
                        <Navbar />
                        <div className="flex-grow pt-16"> {/* Main content area */}
                            <Routes>
                                <Route path="/" element={<LandingPage />} />
                                <Route path="/scoring-guide" element={<ScoringExplained />} />
                                <Route path="/table-predictor" element={
                                    <ProtectedRoute>
                                        <TablePredictions />
                                    </ProtectedRoute>
                                } />
                                <Route path="/match-predictor" element={
                                    <ProtectedRoute>
                                        <MatchPredictions />
                                    </ProtectedRoute>
                                } />
                                <Route path="/performance" element={
                                    <ProtectedRoute>
                                        <Performance />
                                    </ProtectedRoute>
                                } />
                                <Route path="/leagues" element={
                                    <ProtectedRoute>
                                        <Leagues />
                                    </ProtectedRoute>
                                } />
                                <Route path="/leaderboard/match-predictor" element={
                                    <ProtectedRoute>
                                        <MatchPredictorLeaderboard />
                                    </ProtectedRoute>
                                } />
                                <Route path="/leaderboard/table-predictor" element={
                                    <ProtectedRoute>
                                        <TablePredictorLeaderboard />
                                    </ProtectedRoute>
                                } />
                                <Route path="/account-management" element={
                                    <ProtectedRoute>
                                        <AccountManagement />
                                    </ProtectedRoute>
                                } />
                                <Route path="/signup" element={
                                    <PublicOnlyRoute>
                                        <SignUp />
                                    </PublicOnlyRoute>
                                } />
                                <Route path="/login" element={
                                    <PublicOnlyRoute>
                                        <Login />
                                    </PublicOnlyRoute>
                                } />
                                <Route path="*" element={<NotFoundPage />} /> {/* 404 page */}
                            </Routes>
                        </div>
                        <footer className="bg-premier-purple text-white p-4 text-center">
                            <p>For any queries or bugs, please email: <a href="mailto:PredictorPremier@gmail.com" className="underline">PredictorPremier@gmail.com</a></p>
                            <p>Site developed by: Alex Redshaw </p>
                            <p>&copy; {new Date().getFullYear()} Redshaw Premier Predictor. All rights reserved.</p>
                        </footer>
                    </div>
                </Router>
            </UserProvider>
        </AuthProvider>
    );
}

export default App;